import { useMemo } from "react";
import { AIValidationResult } from "src/hooks/useDocumentAI";
import { getPreferredValue } from "../utils/aiSuggestionHelpers";

/**
 * Hook to process address AI results for a person.
 *
 * @param personId - The ID of the person to look up AI results for
 * @param aiResultsMap - Map of AI validation results
 * @returns Object with bestAddressAI and suggestedAddress
 */
export const useAddressAI = (
  personId: string | null | undefined,
  aiResultsMap?: Record<string, AIValidationResult> | null
) => {
  return useMemo(() => {
    if (!personId || !aiResultsMap) {
      return { bestAddressAI: null, suggestedAddress: null };
    }

    // Check for full address AI result using person:{person_id}:full_address format
    const fullAddressAI = aiResultsMap[`person:${personId}:full_address`];

    // If no full address result, return null
    if (!fullAddressAI) {
      return { bestAddressAI: null, suggestedAddress: null };
    }

    // Get the suggested address value directly from full_address result
    const suggestedAddress = getPreferredValue(fullAddressAI);

    return {
      bestAddressAI: fullAddressAI,
      suggestedAddress,
    };
  }, [personId, aiResultsMap]);
};
